import React, { useEffect, useState, useCallback } from 'react';
import api from '../../lib/api';
import {
  Card, CardContent, CardHeader, CardTitle,
} from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend,
  CartesianGrid, PieChart, Pie, Cell, LineChart, Line,
} from 'recharts';
import {
  BarChart3, Loader2, RefreshCw, Send, CheckCheck, Eye, MousePointerClick,
  Flame, Activity, AlertTriangle, Mail,
} from 'lucide-react';
import { toast } from 'sonner';

const STOP_REASON_COLORS = {
  completed: '#10b981',
  hot_via_click: '#f97316',
  hot_or_replied: '#ef4444',
  manual: '#6366f1',
  lead_inactive: '#94a3b8',
  unknown: '#64748b',
};

const formatPct = (n) => `${(n || 0).toFixed(1)}%`;

const SummaryCard = ({ label, value, sub, icon: Icon, tone = 'default', testid }) => {
  const toneStyles = {
    default: 'bg-card border-border',
    success: 'bg-emerald-500/5 border-emerald-500/20',
    warning: 'bg-amber-500/5 border-amber-500/20',
    danger: 'bg-red-500/5 border-red-500/20',
    info: 'bg-sky-500/5 border-sky-500/20',
  };
  const iconStyles = {
    default: 'text-muted-foreground',
    success: 'text-emerald-500',
    warning: 'text-amber-500',
    danger: 'text-red-500',
    info: 'text-sky-500',
  };
  return (
    <Card className={`${toneStyles[tone]} transition-colors`} data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="space-y-1 min-w-0">
            <p className="text-[10px] uppercase font-semibold text-muted-foreground tracking-wide">{label}</p>
            <p className="text-2xl font-bold tabular-nums leading-tight">{value}</p>
            {sub && <p className="text-[11px] text-muted-foreground truncate">{sub}</p>}
          </div>
          {Icon && <Icon className={`h-5 w-5 ${iconStyles[tone]} shrink-0`} />}
        </div>
      </CardContent>
    </Card>
  );
};

export const AnalyticsTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [days, setDays] = useState('30');

  const fetchAnalytics = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    try {
      const r = await api.get(`/smb/outreach/automation/analytics?days=${days}`);
      if (r.data.success) setData(r.data.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load analytics');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [days]);

  useEffect(() => { fetchAnalytics(false); }, [fetchAnalytics]);

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="analytics-loading">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  const { summary, funnel, channels, stop_reasons, time_series } = data;

  const stopReasonChart = Object.entries(stop_reasons || {}).map(([k, n]) => ({
    name: k.replace(/_/g, ' '),
    value: n,
    fill: STOP_REASON_COLORS[k] || '#94a3b8',
  }));

  const channelChart = [
    { channel: 'WhatsApp', ...(channels?.whatsapp || {}) },
    { channel: 'Email', ...(channels?.email || {}) },
  ];

  return (
    <div className="space-y-5 animate-fade-in" data-testid="analytics-tab">
      {/* Toolbar */}
      <Card className="bg-violet-500/5 border-violet-500/20">
        <CardContent className="p-3 flex items-start gap-3 flex-wrap">
          <BarChart3 className="h-5 w-5 text-violet-500 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm">Outreach funnel &amp; conversion</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              See where leads drop off across <strong>M1 → M2 → M3</strong>, which channel is delivering, and which stop reasons are most common.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={days} onValueChange={setDays}>
              <SelectTrigger className="w-[140px] h-8 text-xs" data-testid="analytics-window-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
                <SelectItem value="0">All time</SelectItem>
              </SelectContent>
            </Select>
            <Button
              size="sm"
              variant="outline"
              onClick={() => fetchAnalytics(true)}
              disabled={refreshing}
              data-testid="analytics-refresh-btn"
            >
              {refreshing
                ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="analytics-summary-grid">
        <SummaryCard label="Total Jobs" value={summary.total_jobs} sub={`${summary.active_jobs} active`} icon={Activity} tone="info" testid="card-total-jobs" />
        <SummaryCard label="Messages Sent" value={summary.total_sent} sub={summary.total_failed ? `${summary.total_failed} failed` : 'No failures'} icon={Send} tone="default" testid="card-total-sent" />
        <SummaryCard label="Delivered" value={summary.total_delivered} sub={summary.total_sent ? formatPct(summary.total_delivered / summary.total_sent * 100) : '—'} icon={CheckCheck} tone="info" testid="card-total-delivered" />
        <SummaryCard label="Read" value={summary.total_read} sub={summary.total_sent ? formatPct(summary.total_read / summary.total_sent * 100) : '—'} icon={Eye} tone="success" testid="card-total-read" />
        <SummaryCard label="Clicks" value={summary.total_clicked} sub={`${formatPct(summary.overall_click_rate)} CTR`} icon={MousePointerClick} tone="success" testid="card-total-clicks" />
        <SummaryCard label="Hot Leads" value={summary.hot_leads} sub="Auto-stopped" icon={Flame} tone="danger" testid="card-hot-leads" />
      </div>

      {/* Funnel — sends → delivered → read → clicked per message */}
      <Card data-testid="funnel-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><BarChart3 className="h-4 w-4 text-violet-500" />Funnel by Message</CardTitle>
        </CardHeader>
        <CardContent>
          {summary.total_sent === 0 ? (
            <p className="text-xs text-muted-foreground py-8 text-center" data-testid="funnel-empty">No messages sent in this window yet.</p>
          ) : (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={funnel} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="message" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 12 }}
                    cursor={{ fill: 'hsl(var(--muted) / 0.4)' }}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="sent" fill="#6366f1" name="Sent" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="delivered" fill="#0ea5e9" name="Delivered" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="read" fill="#10b981" name="Read" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="clicked" fill="#f59e0b" name="Clicked" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Per-message rates table */}
      <Card data-testid="rates-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Conversion Rates</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="rates-table">
              <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-4 py-2 text-left font-semibold">Message</th>
                  <th className="px-4 py-2 text-right font-semibold">Sent</th>
                  <th className="px-4 py-2 text-right font-semibold">Failed</th>
                  <th className="px-4 py-2 text-right font-semibold">Delivered</th>
                  <th className="px-4 py-2 text-right font-semibold">Read</th>
                  <th className="px-4 py-2 text-right font-semibold">Clicks</th>
                  <th className="px-4 py-2 text-right font-semibold">CTR</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {funnel.map((f) => (
                  <tr key={f.message} data-testid={`rate-row-${f.message}`} className="hover:bg-muted/20 transition-colors">
                    <td className="px-4 py-2.5 font-medium">
                      <Badge variant="outline" className="font-mono">{f.message}</Badge>
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{f.sent}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{f.failed > 0 ? <span className="text-red-500">{f.failed}</span> : <span className="text-muted-foreground">0</span>}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">
                      {f.delivered} <span className="text-[10px] text-muted-foreground">({formatPct(f.delivered_rate)})</span>
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums">
                      {f.read} <span className="text-[10px] text-muted-foreground">({formatPct(f.read_rate)})</span>
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{f.clicked}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums font-semibold">
                      <span className={f.click_rate >= 10 ? 'text-emerald-500' : f.click_rate > 0 ? 'text-amber-500' : 'text-muted-foreground'}>
                        {formatPct(f.click_rate)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Side-by-side: Channel breakdown + Stop reasons */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card data-testid="channels-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><Mail className="h-4 w-4 text-sky-500" />Channel Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={channelChart} layout="vertical" margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
                  <YAxis dataKey="channel" type="category" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" width={80} />
                  <Tooltip
                    contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 12 }}
                    cursor={{ fill: 'hsl(var(--muted) / 0.4)' }}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="sent" fill="#10b981" name="Sent" stackId="a" />
                  <Bar dataKey="failed" fill="#ef4444" name="Failed" stackId="a" />
                  <Bar dataKey="skipped" fill="#94a3b8" name="Skipped" stackId="a" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="stop-reasons-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-500" />Why Jobs Stopped</CardTitle>
          </CardHeader>
          <CardContent>
            {stopReasonChart.length === 0 ? (
              <p className="text-xs text-muted-foreground py-8 text-center">No stopped jobs in this window.</p>
            ) : (
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={stopReasonChart} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75} paddingAngle={2}>
                      {stopReasonChart.map((entry, idx) => (
                        <Cell key={idx} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 12 }}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Time series — only when window <= 90 days */}
      {time_series && time_series.length > 0 && (
        <Card data-testid="timeseries-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><Activity className="h-4 w-4 text-violet-500" />Daily Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={time_series} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 12 }}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="sent" stroke="#6366f1" strokeWidth={2} name="Sent" dot={false} />
                  <Line type="monotone" dataKey="clicked" stroke="#f59e0b" strokeWidth={2} name="Clicks" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AnalyticsTab;
